import React, { Fragment, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import PostRequest from "../../services/PostRequest";
import moment from "moment";
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useDispatch, useSelector } from "react-redux";
import { asinView, getAsinList, setAsin } from "../../reducer/asin/Asin.action";
import { showToaster } from "../../common/common";
import { McaMenu, McaMenuItem, McaMenuSeparator } from "../../components/mca/Menu";
import { McaPagination, PAGE_SIZE } from "../../components/mca";

const CountryFlagCell = ({ countryCode, domain }) => {
  const countryMap = {
    AU: "🇦🇺",
    US: "🇺🇸",
    UK: "🇬🇧",
  };

  return (
    <span className="flagcell">
      <span className="fl">{countryMap[countryCode] || ""}</span>
      {domain}
    </span>
  );
};

const RowActionsMenu = ({ row, isOfficeworks, isRepco, params, dispatch, onEdit }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const itemLabel = isOfficeworks || isRepco ? "SKU" : "ASIN";

  const handleEdit = async () => {
    try {
      dispatch(asinView(row.original));
      dispatch(setAsin(true));
      setMenuOpen(false);
      onEdit?.();
    } catch (error) {
      console.log("error", error);
    }
  };

  const handleDelete = async () => {
    if (
      !window.confirm(
        `Are you sure you want to delete this ${itemLabel.toLowerCase()}?`
      )
    )
      return;

    try {
      if (isRepco) {
        await PostRequest("/schedules/repco/sku/delete/", {
          sku_id: row.original.id,
        });
      } else if (isOfficeworks) {
        await PostRequest("/schedules/officeworks/sku/delete/", {
          sku_id: row.original.id,
        });
      } else {
        await PostRequest("/schedules/asin/delete/", {
          asin_id: row.original.id,
        });
      }
      dispatch(getAsinList({ collection_id: params.id }));
      showToaster(`${itemLabel} deleted successfully!`);
      setMenuOpen(false);
    } catch (error) {
      console.log("error", error.message);
      showToaster(error.message, "error");
    }
  };

  return (
    <div className="mca-row-actions">
      <McaMenu
        trigger={
          <button
            className="mca-btn mca-btn--ghost mca-btn--icon mca-btn--sm"
            aria-label="More"
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            >
              <circle cx="12" cy="5" r="1" />
              <circle cx="12" cy="12" r="1" />
              <circle cx="12" cy="19" r="1" />
            </svg>
          </button>
        }
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
      >
        <McaMenuItem onClick={handleEdit}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M4 20h4l10-10a2 2 0 0 0-3-3L5 17v3Z" />
          </svg>
          Edit
        </McaMenuItem>
        <McaMenuSeparator />
        <McaMenuItem isDanger onClick={handleDelete}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M4 7h16" />
            <path d="M9 7V4h6v3" />
            <path d="M6 7l1 13h10l1-13" />
          </svg>
          Delete
        </McaMenuItem>
      </McaMenu>
    </div>
  );
};

export const AsinsTableNew = ({ requestType = "amazon", onEdit }) => {
  const _asinList = useSelector((item) => item.asinReducer.asinList);
  const _officeworksStoreList = useSelector(
    (state) => state.zipCodeReducer.officeworksStoreList
  );
  const [loader, setLoader] = useState(false);
  const [asins, setAsins] = useState([]);
  const [skip, setSkip] = useState(0);

  const dispatch = useDispatch();
  const params = useParams();

  const isOfficeworks = requestType === "officeworks";
  const isRepco = requestType === "repco";
  const itemLabel = isOfficeworks || isRepco ? "SKU" : "ASIN";

  const amazonColumns = [
    {
      id: "checkbox",
      header: ({ table }) => (
        <label className="mca-check">
          <input
            type="checkbox"
            checked={table.getIsAllRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
            aria-label="Select all"
          />
          <span className="mca-check__box">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 6 9 17l-5-5" />
            </svg>
          </span>
        </label>
      ),
      cell: ({ row }) => (
        <label className="mca-check">
          <input
            type="checkbox"
            checked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
            aria-label={`Select ${row.original.asin}`}
          />
          <span className="mca-check__box">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 6 9 17l-5-5" />
            </svg>
          </span>
        </label>
      ),
      size: 46,
    },
    {
      accessorKey: "asin",
      header: "ASIN",
      cell: (info) => <span className="mca-cell-mono cell-strong">{info.getValue()}</span>,
    },
    {
      accessorKey: "domain.domain",
      header: "Domain",
      cell: ({ row }) => (
        <CountryFlagCell
          countryCode={row.original.domain.country_code}
          domain={row.original.domain.domain}
        />
      ),
    },
    {
      accessorKey: "customer_postcode.zipcode",
      header: "Postcode",
      cell: (info) => <span className="cell-muted">{info.getValue() || "—"}</span>,
      className: "hide-md",
    },
    {
      accessorKey: "output",
      header: "Output",
      cell: (info) => (
        <span className="mca-badge mca-badge--soft-muted">{info.getValue()}</span>
      ),
      className: "hide-md",
    },
    {
      accessorKey: "created_at",
      header: "Created",
      cell: (info) => (
        <span className="cell-muted" style={{ fontSize: "13px" }}>
          {moment(info.getValue()).format("DD MMM yyyy")}
        </span>
      ),
      className: "hide-md",
    },
    {
      id: "actions",
      header: () => <div style={{ textAlign: "right" }}>Actions</div>,
      cell: ({ row }) => (
        <RowActionsMenu
          row={row}
          isOfficeworks={isOfficeworks}
          params={params}
          dispatch={dispatch}
          onEdit={onEdit}
        />
      ),
    },
  ];

  const officeworksColumns = [
    {
      id: "checkbox",
      header: ({ table }) => (
        <label className="mca-check">
          <input
            type="checkbox"
            checked={table.getIsAllRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
            aria-label="Select all"
          />
          <span className="mca-check__box">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 6 9 17l-5-5" />
            </svg>
          </span>
        </label>
      ),
      cell: ({ row }) => (
        <label className="mca-check">
          <input
            type="checkbox"
            checked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
            aria-label={`Select ${row.original.asin}`}
          />
          <span className="mca-check__box">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 6 9 17l-5-5" />
            </svg>
          </span>
        </label>
      ),
      size: 46,
    },
    {
      accessorKey: "asin",
      header: "SKU",
      cell: (info) => <span className="mca-cell-mono cell-strong">{info.getValue()}</span>,
    },
    {
      accessorKey: "custom_id",
      header: "Domain",
      cell: (info) => {
        const storeId = info.getValue();
        const store = (_officeworksStoreList || []).find(
          (s) => s.store_id === storeId
        );
        return <span className="cell-strong">{store ? store.store_name : storeId}</span>;
      },
    },
    {
      accessorKey: "customer_postcode.zipcode",
      header: "Postcode",
      cell: (info) => <span className="cell-muted">{info.getValue() || "—"}</span>,
      className: "hide-md",
    },
    {
      accessorKey: "created_at",
      header: "Created",
      cell: (info) => (
        <span className="cell-muted" style={{ fontSize: "13px" }}>
          {moment(info.getValue()).format("DD MMM yyyy")}
        </span>
      ),
      className: "hide-md",
    },
    {
      id: "actions",
      header: () => <div style={{ textAlign: "right" }}>Actions</div>,
      cell: ({ row }) => (
        <RowActionsMenu
          row={row}
          isOfficeworks={isOfficeworks}
          params={params}
          dispatch={dispatch}
          onEdit={onEdit}
        />
      ),
    },
  ];

  const repcoColumns = [
    officeworksColumns[0],
    {
      accessorKey: "asin",
      header: "Product Code",
      cell: (info) => <span className="mca-cell-mono cell-strong">{info.getValue()}</span>,
    },
    {
      accessorKey: "customer_postcode.zipcode",
      header: "Postcode",
      cell: (info) => <span className="cell-muted">{info.getValue() || "—"}</span>,
      className: "hide-md",
    },
    {
      accessorKey: "created_at",
      header: "Created",
      cell: (info) => (
        <span className="cell-muted" style={{ fontSize: "13px" }}>
          {moment(info.getValue()).format("DD MMM yyyy")}
        </span>
      ),
      className: "hide-md",
    },
    {
      id: "actions",
      header: () => <div style={{ textAlign: "right" }}>Actions</div>,
      cell: ({ row }) => (
        <RowActionsMenu
          row={row}
          isOfficeworks={isOfficeworks}
          isRepco={isRepco}
          params={params}
          dispatch={dispatch}
          onEdit={onEdit}
        />
      ),
    },
  ];

  const columns = isRepco
    ? repcoColumns
    : isOfficeworks
    ? officeworksColumns
    : amazonColumns;

  const tableInstance = useReactTable({
    columns,
    data: asins,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: PAGE_SIZE } },
  });

  const loadAsins = async () => {
    const data = {
      collection_id: params?.id,
      limit: 100,
      skip: 0,
    };
    try {
      setLoader(true);
      setSkip(0);
      dispatch(getAsinList(data));
    } catch (error) {
      console.log(error);
    } finally {
      setLoader(false);
    }
  };

  const loadMoreAsinList = () => {
    const data = {
      limit: 100,
      collection_id: params?.id,
      skip: _asinList?.length,
    };
    try {
      setLoader(true);
      dispatch(getAsinList(data, true));
      setSkip(_asinList?.length);
    } catch (error) {
      console.log(error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    loadAsins();
  }, []);

  useEffect(() => {
    setAsins(_asinList);
  }, [_asinList]);

  return (
    <>
      <div className="mca-table-wrap">
        <table className="mca-table">
          <thead>
            {tableInstance?.getHeaderGroups()?.map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup?.headers?.map((header) => (
                  <th
                    key={header.id}
                    style={{
                      width:
                        header.getSize() !== 150
                          ? header.getSize()
                          : undefined,
                      textAlign: header.id === "actions" ? "right" : undefined,
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {loader ? (
              <tr>
                <td colSpan={columns.length} style={{ textAlign: "center", padding: "24px" }}>
                  Loading...
                </td>
              </tr>
            ) : asins.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  style={{ border: "none", textAlign: "center", padding: "48px 16px" }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <svg
                      width="48"
                      height="48"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ color: "var(--mca-muted)" }}
                    >
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" />
                      <path d="M12 6v6" />
                      <circle cx="12" cy="15" r="1" />
                    </svg>
                    <h4 style={{ margin: 0, color: "var(--mca-muted)" }}>
                      No {itemLabel.toLowerCase()}s yet
                    </h4>
                    <p style={{ margin: 0, fontSize: "14px", color: "var(--mca-muted)" }}>
                      Create your first {itemLabel.toLowerCase()} to get started
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              tableInstance.getRowModel().rows?.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells()?.map((cell) => (
                    <td key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Table footer: 50 per page; "Load more" fetches additional records from the server. */}
      {asins.length > 0 && (
        <div className="table-foot">
          <span style={{ display: "inline-flex", gap: "12px", alignItems: "center" }}>
            Showing {tableInstance.getState().pagination.pageIndex * PAGE_SIZE + 1}–
            {Math.min(
              (tableInstance.getState().pagination.pageIndex + 1) * PAGE_SIZE,
              asins.length
            )}{" "}
            of {asins.length} loaded
            {_asinList.length >= 100 && (
              <button
                className="mca-btn mca-btn--outline mca-btn--sm"
                onClick={loadMoreAsinList}
                disabled={loader}
                type="button"
              >
                {loader ? "Loading..." : "Load more"}
              </button>
            )}
          </span>
          <McaPagination
            page={tableInstance.getState().pagination.pageIndex + 1}
            totalPages={tableInstance.getPageCount()}
            onChange={(p) => tableInstance.setPageIndex(p - 1)}
          />
        </div>
      )}
    </>
  );
};
